/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import "./SectionCards.css";
import { SectionCardsProps, SectionType } from "../../types/socialJournal";
import { getWeeklyStatus } from "../../services/dashboard";
import useAuth from "../../hooks/useAuth";
import { HomeTherapyData, TherapyDataEntry } from "../../types/homeTypes";
import { Preferences } from "@capacitor/preferences";
import { generateDateRange } from "../../utils/dateHelpers";
import { IonSpinner, IonToast } from "@ionic/react";
const SectionCards: React.FC<SectionCardsProps> = ({
  activeSection,
  onSectionChange,
  onTherapyDataSelect,
  journalCategories,
  templateCards,
  onTemplateSelect,
  toaster,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { startDate, endDate } = generateDateRange("Week");
  const today = new Date();
  const todayEnCa = today.toLocaleDateString("en-CA");
  useEffect(() => {
    fetchWeeklyStatus();
  }, []);

  const handleSectionClick = async (sectionType: SectionType) => {
    if (sectionType === activeSection) {
      onSectionChange(null);
      return;
    }
    onTemplateSelect?.(sectionType as string); // Clear template selection when changing section
    onSectionChange(sectionType);

    // Directly fetch weekly therapy data when Inspiration is clicked
    if (sectionType === "progress") {
      await fetchWeeklyTherapyData();
    }
  };
  const fetchWeeklyStatus = async () => {
    try {
      if (!user) {
        setError("User profile ID is missing.");
        return;
      }
      const response: HomeTherapyData = await getWeeklyStatus(user.profile_id, {
        date: todayEnCa,
        startDate: startDate,
        endDate: endDate,
        showPercentage: true, // Important: Get percentage data
      });
      const therapies: TherapyDataEntry[] =
        response.therapyData.filter(
          (therapy: any) => therapy.hadConnectedDevice
        ) || [];

      // Store to Preferences for offline access
      await Preferences.set({
        key: "therapyDataWeekly",
        value: JSON.stringify({ therapyData: therapies }),
      });
    } catch (err) {
      console.error("Error fetching weekly therapy data:", err);
      setError("Failed to load weekly therapy data");
    }
  };
  const fetchWeeklyTherapyData = async () => {
    if (!user?.profile_id) return;
    setLoading(true);
    setError(null);
    const { value: therapyResponse }: any = await Preferences.get({
      key: "therapyDataWeekly",
    });

    if (therapyResponse) {
      const therapies = JSON.parse(therapyResponse)["therapyData"];
      onTherapyDataSelect?.({
        period: "weekly",
        dateRange: { start: startDate, end: endDate },
        therapies: therapies,
      });
      setLoading(false);

      // Ensure we have proper therapy items
      if (therapies.length === 0) {
        toaster.info("You have no connected BiWaze® devices.", 3000, "bottom");
      }
      // Update parent component
      if (onTherapyDataSelect) {
        onTherapyDataSelect({
          period: "weekly",
          dateRange: { start: startDate, end: endDate },
          therapies, // Pass the raw therapy data for display
        });
      }
    } else {
      // await fetchWeeklyStatus();
      // setLoading(true);
    }
  };

  return (
    <div className="mb-2 pt-4">
      <div className="cards-scroll">
        {(templateCards || []).map((card, idx) => {
          const isActive = activeSection === card.key;
          const isLoading = isActive && loading;
          const isFirst = idx === 0;
          const isLast = idx === (templateCards?.length ?? 0) - 1;
          const extraClass = `${isFirst ? "ml-3 " : ""}${isLast ? " mr-3" : ""}`;

          return (
            <div
              key={card.key}
              onClick={() => {
                handleSectionClick(card.key as SectionType);
              }}
              className={`section-card ${card.className} ${extraClass}${
                isActive ? "section-card--active " : ""
              }${isLoading ? "schimmer" : ""}`}
              style={{
                ...(isLoading ? { pointerEvents: "none" } : {}),
              }}
            >
              <div
                className={`card-title ${isActive ? "card-title--active" : ""}`}
              >
                {card.title}{" "}
                {isLoading && <IonSpinner name="dots" className="w-4 h-4" />}
              </div>
              <div className="card-description">{card.description}</div>
            </div>
          );
        })}
      </div>
      <IonToast
        isOpen={!!error}
        onDidDismiss={() => setError(null)}
        message={error || ""}
        duration={3000}
        color="danger"
      />
    </div>
  );
};

export default SectionCards;
